/**
 * BlockLibraryBrowserDialog — Import / Pick browser over the Block Examples
 * Library (approved mock: library-browser-default.html). Merges the SYSTEM
 * tier (root systemBlockLibrary, read-only catalogue) with the project tier
 * («My»). Selection + «Import N to project library» applies to system
 * examples only — importing copies the doc into `projects/{pid}/blockLibrary`
 * with `systemExampleId` provenance; already-imported cards show the Imported
 * badge and cannot be re-selected. Add defaults to a staged draft; immediate
 * AI remains an explicit secondary action in the details footer.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import type { Theme } from '@mui/material/styles';
import { BaseDialog } from '@modules/ui-components/dialogs/BaseDialog';
import { DialogHeader } from '@modules/ui-components/dialogs/DialogHeader';
import { DialogContent } from '@modules/ui-components/dialogs/DialogContent';
import { DialogFooter } from '@modules/ui-components/dialogs/DialogFooter';
import {
  assessLibraryExampleRenderability,
  computeContractFit,
  type BlockLibraryExampleDoc,
} from '@modules/shared-types';
import { BLOCK_TYPE_COLORS } from '@modules/reactFlow/services/block/BlockPreviewRenderer';
import { STRATEGY_CANVAS } from '@test-kit/selectors/strategy-canvas';
import { ExampleDetailsView } from './ExampleDetailsView';

export type BlockLibraryBrowserFilter = 'all' | 'system' | 'my';

export interface BlockLibraryBrowserDialogProps {
  open: boolean;
  /** System tier (root systemBlockLibrary). */
  systemExamples: BlockLibraryExampleDoc[];
  /** Project tier — also the source of Imported provenance badges. */
  projectExamples: BlockLibraryExampleDoc[];
  /** Per-card quick pick — insert on the canvas via the example:: flow. */
  onAddToCanvas: (exampleId: string, source: 'project' | 'system') => void;
  onAdaptImmediately?: (exampleId: string, source: 'project' | 'system') => void;
  /** Import the selected SYSTEM example ids into the project library. */
  onImport: (systemExampleIds: string[]) => void | Promise<void>;
  onClose: () => void;
  /** Current strategy variables — renders the advisory fit line per card (AC-P1-076). */
  strategyVariables?: Record<string, unknown>;
}

interface BrowserCard {
  doc: BlockLibraryExampleDoc;
  source: 'project' | 'system';
  /** For system cards: the project copy exists (systemExampleId provenance). */
  imported: boolean;
}

const FILTERS: Array<{ key: BlockLibraryBrowserFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'system', label: 'System' },
  { key: 'my', label: 'My' },
];

export const BlockLibraryBrowserDialog: React.FC<BlockLibraryBrowserDialogProps> = ({
  open,
  systemExamples,
  projectExamples,
  onAddToCanvas,
  onAdaptImmediately,
  onImport,
  onClose,
  strategyVariables,
}) => {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<BlockLibraryBrowserFilter>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  // In-dialog details swap (FP-4: the browser is already a modal — details
  // replace its content with a back row, never stack a second dialog).
  const [detail, setDetail] = useState<BrowserCard | null>(null);

  const importedSystemIds = useMemo(() => new Set(
    projectExamples
      .map((example) => example.systemExampleId)
      .filter((id): id is string => Boolean(id)),
  ), [projectExamples]);

  const cards = useMemo<BrowserCard[]>(() => {
    // One card per EXAMPLE (review #P2-3): an imported copy is represented by
    // the badged System card alone — a project doc with systemExampleId never
    // renders its own «My» twin (the palette stays the surface where the copy
    // itself is actionable).
    const systemIds = new Set(systemExamples.map((doc) => doc.id));
    const merged: BrowserCard[] = [
      ...systemExamples.map((doc) => ({
        doc,
        source: 'system' as const,
        imported: importedSystemIds.has(doc.id),
      })),
      ...projectExamples
        .filter((doc) => !(doc.systemExampleId && systemIds.has(doc.systemExampleId)))
        .map((doc) => ({ doc, source: 'project' as const, imported: false })),
    ];
    const needle = query.trim().toLowerCase();
    return merged.filter(({ doc, source }) => {
      if (filter === 'system' && source !== 'system') return false;
      if (filter === 'my' && source !== 'project') return false;
      if (!needle) return true;
      const haystack = [doc.name, doc.description, doc.scopeMetadata.blockType, ...(doc.tags ?? [])]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [systemExamples, projectExamples, importedSystemIds, query, filter]);

  // AC-P2-015: bulk selection of everything importable (system, not yet
  // imported, renderable) — the footer button then imports the whole set.
  const selectableSystemIds = useMemo(() => cards
    .filter((card) => card.source === 'system'
      && !card.imported
      && assessLibraryExampleRenderability(card.doc).renderable)
    .map((card) => card.doc.id), [cards]);

  const toggleSelect = useCallback((systemExampleId: string) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(systemExampleId)) next.delete(systemExampleId);
      else next.add(systemExampleId);
      return next;
    });
  }, []);

  const handleImport = useCallback(async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setImporting(true);
    try {
      await onImport(ids);
      setSelected(new Set());
    } finally {
      setImporting(false);
    }
  }, [selected, onImport]);

  const renderCard = ({ doc, source, imported }: BrowserCard) => {
    const accent = BLOCK_TYPE_COLORS[doc.scopeMetadata.blockType] ?? BLOCK_TYPE_COLORS.custom ?? '#607D8B';
    const isSelected = source === 'system' && selected.has(doc.id);
    // AC-P1-036: a broken envelope renders inert with the reason named.
    const renderability = assessLibraryExampleRenderability(doc);
    // AC-P1-076: advisory contract-fit line against the CURRENT strategy.
    const fit = strategyVariables && renderability.renderable
      ? computeContractFit(doc, strategyVariables as never)
      : [];
    const fitCounts = fit.reduce(
      (acc, entry) => {
        if (entry.resolution === 'bind') acc.mapped += 1;
        else if (entry.resolution === 'needs-adapt') acc.needsAdapt += 1;
        else acc.created += 1;
        return acc;
      },
      { mapped: 0, created: 0, needsAdapt: 0 },
    );
    return (
      <Box
        key={`${source}-${doc.id}`}
        data-testid={STRATEGY_CANVAS.libraryBrowserCard(doc.id)}
        sx={{
          position: 'relative',
          border: 1,
          borderColor: isSelected ? 'primary.main' : 'divider',
          borderLeft: 4,
          borderLeftColor: accent,
          borderRadius: 1,
          p: 1.5,
          bgcolor: isSelected ? 'action.selected' : 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
        }}
      >
        {source === 'system' && (
          <Box
            data-testid={STRATEGY_CANVAS.libraryBrowserSelect(doc.id)}
            sx={{ position: 'absolute', top: 4, right: 4 }}
          >
            {/* The input itself owns the toggle and stays in the tab order —
                a wrapper-onClick + tabIndex={-1} combo made the primary action
                keyboard-unreachable (review #P2-2, WCAG 2.1.1). */}
            <Checkbox
              size="small"
              checked={isSelected}
              disabled={imported || !renderability.renderable}
              onChange={() => toggleSelect(doc.id)}
              inputProps={{ 'aria-label': `Select ${doc.name} for import` }}
            />
          </Box>
        )}
        <Typography variant="body2" sx={{ fontWeight: 600, pr: source === 'system' ? 4 : 0 }}>
          {doc.name}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ my: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
          {source === 'system' ? (
            <Chip label="System" size="small" variant="outlined" color="primary" sx={{ height: 18, fontSize: 10 }} />
          ) : (
            <Chip label="My" size="small" sx={{ height: 18, fontSize: 10 }} />
          )}
          {imported && (
            <Chip
              data-testid={STRATEGY_CANVAS.libraryBrowserImported(doc.id)}
              label="Imported"
              size="small"
              variant="outlined"
              color="success"
              sx={{ height: 18, fontSize: 10 }}
            />
          )}
          <Chip label={doc.scopeMetadata.blockType} size="small" sx={{ height: 18, fontSize: 10, fontFamily: 'monospace' }} />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {doc.screensCount} screens · contract {doc.contractSummary.inputs}→{doc.contractSummary.outputs}
          {strategyVariables && renderability.renderable && fit.length > 0 && (
            <> · fit: {fitCounts.mapped} mapped · {fitCounts.created} new
              {fitCounts.needsAdapt > 0 ? ` · ${fitCounts.needsAdapt} need adapt` : ''}</>
          )}
        </Typography>
        {!renderability.renderable && (
          <Typography variant="caption" color="error" component="div">
            Cannot be inserted: {renderability.reason}
          </Typography>
        )}
        {doc.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              mt: 0.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {doc.description}
          </Typography>
        )}
        <Box sx={{ mt: 'auto', pt: 1, display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            data-testid={STRATEGY_CANVAS.libraryBrowserQuickAdd(doc.id)}
            onClick={() => onAddToCanvas(doc.id, source)}
            disabled={!renderability.renderable}
            title={renderability.renderable ? undefined : renderability.reason}
          >
            Add draft
          </Button>
          {/* Details opens even for broken examples — the reason must stay
              discoverable (FP-7). In-dialog swap, never a second modal (FP-4). */}
          <Button
            size="small"
            variant="text"
            data-testid={STRATEGY_CANVAS.libraryBrowserDetails(doc.id)}
            onClick={() => setDetail({ doc, source, imported })}
          >
            Details
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      mode="modal"
      data-testid={STRATEGY_CANVAS.libraryBrowserDialog}
      slotProps={{ paper: { 'aria-label': 'Block library' } }}
    >
      <DialogHeader title="Block library" onClose={onClose} />
      {detail ? (
        <>
          <DialogContent maxHeight="calc(100vh - 240px)">
            <Stack spacing={1.5}>
              <Box>
                <Button
                  size="small"
                  startIcon={<ChevronLeftIcon />}
                  data-testid={STRATEGY_CANVAS.libraryBrowserDetailsBack}
                  onClick={() => setDetail(null)}
                >
                  Back to list
                </Button>
              </Box>
              <ExampleDetailsView
                example={detail.doc}
                source={detail.source}
                strategyVariables={strategyVariables}
              />
            </Stack>
          </DialogContent>
          <DialogFooter
            helperText="The draft is saved on canvas before example content is adapted."
            primaryAction={{
              label: 'Add draft to canvas',
              onClick: () => onAddToCanvas(detail.doc.id, detail.source),
              disabled: !assessLibraryExampleRenderability(detail.doc).renderable,
              sx: (theme: Theme) => (theme.palette.mode === 'dark' ? {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  boxShadow: 'none',
                },
              } : {}),
              'data-testid': STRATEGY_CANVAS.exampleDetailsAdd,
            } as never}
            secondaryAction={onAdaptImmediately ? {
              label: 'Adapt immediately…',
              variant: 'text',
              onClick: () => onAdaptImmediately(detail.doc.id, detail.source),
              disabled: !assessLibraryExampleRenderability(detail.doc).renderable,
              'data-testid': STRATEGY_CANVAS.libraryBrowserImmediate(detail.doc.id),
            } : undefined}
          />
        </>
      ) : (
        <>
      {/* Grow with the viewport: the DialogContent default is a FIXED 500px
          scroller that clips the grid while the paper still has headroom
          (review #P2-1; strategy-blocks designer invariant 5). Budget =
          dialog margins (64) + header (~52) + toolbar/footer (~124). */}
      <DialogContent maxHeight="calc(100vh - 240px)">
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              data-testid={STRATEGY_CANVAS.libraryBrowserSearch}
              size="small"
              fullWidth
              placeholder="Search examples"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            {FILTERS.map(({ key, label }) => (
              <Chip
                key={key}
                data-testid={STRATEGY_CANVAS.libraryBrowserFilter(key)}
                label={label}
                size="small"
                variant={filter === key ? 'filled' : 'outlined'}
                color={filter === key ? 'primary' : 'default'}
                onClick={() => setFilter(key)}
              />
            ))}
            <Button
              size="small"
              data-testid={STRATEGY_CANVAS.libraryBrowserSelectAll}
              disabled={selectableSystemIds.length === 0
                || selectableSystemIds.every((id) => selected.has(id))}
              onClick={() => setSelected(new Set([...selected, ...selectableSystemIds]))}
            >
              Select all
            </Button>
          </Stack>

          {cards.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No examples match. Save a block as an example from the canvas ⋮ menu, or clear the search.
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 1.5,
              }}
            >
              {cards.map(renderCard)}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogFooter
        helperText={`${selected.size} selected · system examples are copied into your project library`}
        primaryAction={{
          label: `Import ${selected.size} to project library`,
          onClick: handleImport,
          disabled: selected.size === 0,
          loading: importing,
          // Same dark-theme solid-primary exemption as InsertBuildingBlockDialog
          // (glassy dark buttons collapse the action hierarchy — invariant #9).
          sx: (theme: Theme) => (theme.palette.mode === 'dark' ? {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
              boxShadow: 'none',
            },
          } : {}),
          'data-testid': STRATEGY_CANVAS.libraryBrowserImport,
        } as never}
      />
        </>
      )}
    </BaseDialog>
  );
};

BlockLibraryBrowserDialog.displayName = 'BlockLibraryBrowserDialog';
